import type {
  AdminUserDto,
  AiDraftProposal,
  AiQuotaDto,
  CatalogItemDto,
  PublicQuoteDto,
  QuoteAiProposal,
  QuoteDto,
  AppSettingsDto,
  AuditEventDto,
  AddressSuggestion,
  CompanyDraft,
  CompanyDto,
  EinvoiceReceptionDto,
  ExpenseDto,
  ExpenseScanResultDto,
  ExpenseSummaryDto,
  InvoiceDto,
  OpenapiUsageDto,
  ReceiptDto,
  ReceiptInput,
  SubscriptionDto,
  PlanId,
  BillingInterval,
  SubscriptionStatus,
  UserDto,
  UserPreferencesDto,
  UsagePeriod,
  PeppolReachabilityDto,
  AccountingSummaryDto,
  BankInstitutionDto,
  BankOverviewDto,
  BankTransactionDto,
  AccountantAccessDto,
  PhoneLineDto,
  SignatureOverviewDto,
  SignatureRequestDto,
  LeadDto,
  LeadStatus,
  AccountantInvitationDto,
  AccountantDossierDto,
  LedgerEntryDto,
  LedgerOverrideDto,
  BankTransactionFilter,
  BankImportResultDto,
  BillingDto,
  DashboardDto,
  AppointmentDto,
  BookingSettingsDto,
  BookingSettingsInput,
  BookingRequestInput,
  ManualAppointmentInput,
  PublicBookingDto,
  ReminderDto,
  ConnectStatusDto,
  SupportTicketInput,
  QuoteOnSiteAcceptInput,
  FeaturesDto,
} from '@bill-pulse/shared';
import { API_ORIGIN, clearSessionToken, isNativeApp, nativeHeaders, saveSessionToken } from '@/lib/native';

export interface ApiErrorDetail {
  field?: string;
  message: string;
}

/** Erreur HTTP portant les détails de validation renvoyés par l'API. */
export class ApiRequestError extends Error {
  /** 0 lorsque la requête n'a jamais atteint le serveur (échec réseau). */
  readonly status: number;
  readonly details: ApiErrorDetail[] | string[] | null;

  constructor(
    status: number,
    message: string,
    details: ApiErrorDetail[] | string[] | null,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.details = details;
  }

  /** Liste à plat des messages, pour l'affichage. */
  get messages(): string[] {
    if (!this.details) return [this.message];
    return this.details.map((detail) =>
      typeof detail === 'string' ? detail : [detail.field, detail.message].filter(Boolean).join(' : '),
    );
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // `content-type: application/json` UNIQUEMENT s'il y a un corps. Envoyé sur une
  // requête vide (logout, send, delete…), Fastify rejette avec « Body cannot be
  // empty when content-type is set to application/json » → 500.
  const headers: Record<string, string> = { ...nativeHeaders(), ...(init.headers as Record<string, string>) };
  // Un FormData (envoi de fichier) fixe lui-même son en-tête multipart et sa frontière.
  if (init.body !== undefined && init.body !== null && !(init.body instanceof FormData)) {
    headers['content-type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetch(`${API_ORIGIN}/api${path}`, {
      ...init,
      // Web : la session est un cookie HTTP-only, indispensable. App : jeton
      // Bearer dans les en-têtes, aucun cookie.
      credentials: isNativeApp ? 'omit' : 'include',
      headers,
    });
  } catch (cause) {
    // fetch ne rejette que sur un échec réseau : DNS, TLS, hors-ligne, requête
    // bloquée. Le dire explicitement plutôt qu'un « une erreur est survenue »
    // qui laisse croire à un refus du serveur.
    throw new ApiRequestError(
      0,
      "Impossible de joindre le serveur. Vérifiez votre connexion réseau, puis réessayez.",
      null,
      cause,
    );
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as
    | { error?: string; details?: ApiErrorDetail[] | string[] | null }
    | null;

  // 413 : refusé par le reverse-proxy avant l'application, qui n'a donc pas pu
  // formuler de message ; le dire en clair plutôt que « Erreur HTTP 413 ».
  if (response.status === 413) {
    throw new ApiRequestError(413, 'Fichier trop volumineux (10 Mo au maximum).', null);
  }
  // App : jeton expiré ou révoqué (déconnexion à distance) → on l'oublie.
  if (response.status === 401 && isNativeApp) await clearSessionToken();
  if (!response.ok) {
    throw new ApiRequestError(
      response.status,
      payload?.error ?? `Erreur HTTP ${response.status}`,
      payload?.details ?? null,
    );
  }
  return payload as T;
}

/** App : le jeton renvoyé à la connexion part au trousseau, pas dans l'état de l'interface. */
async function keepToken({ token, ...user }: UserDto & { token?: string }): Promise<UserDto> {
  if (token) await saveSessionToken(token);
  return user;
}

const body = (data: unknown): RequestInit => ({ body: JSON.stringify(data) });

/** Même plafond que l'API : refusé avant l'envoi, sans gaspiller la connexion mobile. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

function formData(file: File): FormData {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new ApiRequestError(413, 'Fichier trop volumineux (10 Mo au maximum).', null);
  }
  const data = new FormData();
  data.append('file', file, file.name);
  return data;
}

/** Inscription : le compte et son entreprise émettrice, créés ensemble. */
export interface RegisterPayload {
  email: string;
  /** Absent pour une inscription « avec Google ». */
  password?: string;
  firstName?: string;
  lastName?: string;
  company: Record<string, unknown>;
}

export const api = {
  register: (data: RegisterPayload) =>
    request<UserDto & { token?: string }>('/auth/register', { method: 'POST', ...body(data) }).then(keepToken),
  verifyEmail: (token: string) =>
    request<{ verified: true }>('/auth/verify-email', { method: 'POST', ...body({ token }) }),
  resendVerification: () =>
    request<{ sent: boolean; alreadyVerified?: boolean }>('/auth/resend-verification', { method: 'POST' }),
  registrationStatus: () => request<{ open: boolean; google: boolean }>('/auth/registration'),
  googlePending: () => request<{ email: string; firstName: string | null; lastName: string | null } | null>('/auth/google/pending'),
  suggestAddresses: (q: string) => request<AddressSuggestion[]>(`/geo/addresses?${new URLSearchParams({ q })}`),
  /** Recherche SIRET sans compte, pour l'inscription (limitée en débit côté serveur). */
  lookupSiretPublic: (siret: string) => request<CompanyDraft>(`/auth/lookup-siret/${siret}`),
  login: (data: { email: string; password: string }) =>
    request<UserDto & { token?: string }>('/auth/login', { method: 'POST', ...body(data) }).then(keepToken),
  createSupportTicket: (data: SupportTicketInput) =>
    request<{ number: string }>('/support/tickets', { method: 'POST', ...body(data) }),
  cancelIbanChange: (token: string) => request<{ ok: true }>('/auth/iban-cancel', { method: 'POST', ...body({ token }) }),
  forgotPassword: (email: string) => request<{ sent: true }>('/auth/forgot-password', { method: 'POST', ...body({ email }) }),
  resetPassword: (token: string, password: string) =>
    request<{ reset: true }>('/auth/reset-password', { method: 'POST', ...body({ token, password }) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }).finally(clearSessionToken),
  me: () => request<UserDto>('/auth/me'),
  /** Vérifie dans l'annuaire Peppol sous quel identifiant l'entreprise est joignable. */
  companyPeppol: (id: string) => request<PeppolReachabilityDto>(`/companies/${id}/peppol`),
  registerPushDevice: (data: { platform: 'ios' | 'android'; token: string }) =>
    request<void>('/me/push-device', { method: 'PUT', ...body(data) }),
  unregisterPushDevice: () => request<void>('/me/push-device', { method: 'DELETE' }),
  /** Suppression définitive de son compte ; mot de passe redemandé. */
  deleteAccount: (password: string) => request<void>('/me/delete-account', { method: 'POST', ...body({ password }) }).then(clearSessionToken),

  listReceipts: (from?: string, to?: string) =>
    request<ReceiptDto[]>(`/receipts?${new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) })}`),
  createReceipt: (data: ReceiptInput) => request<ReceiptDto>('/receipts', { method: 'POST', ...body(data) }),
  updateReceipt: (id: string, data: ReceiptInput) =>
    request<ReceiptDto>(`/receipts/${id}`, { method: 'PATCH', ...body(data) }),
  deleteReceipt: (id: string) => request<void>(`/receipts/${id}`, { method: 'DELETE' }),

  listExpenses: (filters: { from?: string; to?: string; status?: 'draft' | 'validated' } = {}) =>
    request<ExpenseDto[]>(`/expenses?${new URLSearchParams(Object.entries(filters).filter(([, v]) => v) as [string, string][])}`),
  createExpense: (data: unknown) => request<ExpenseDto>('/expenses', { method: 'POST', ...body(data) }),
  updateExpense: (id: string, data: unknown, validate = false) =>
    request<ExpenseDto>(`/expenses/${id}`, { method: 'PATCH', ...body({ ...(data as object), validate }) }),
  deleteExpense: (id: string) => request<void>(`/expenses/${id}`, { method: 'DELETE' }),
  scanExpense: (file: File) => request<ExpenseScanResultDto>('/expenses/scan', { method: 'POST', body: formData(file) }),
  uploadAttachment: (file: File) =>
    request<{ id: string; name: string; mimeType: string }>('/expenses/attachments', { method: 'POST', body: formData(file) }),
  exchangeRate: (currency: string, date: string) =>
    request<{ rate: number; date: string }>(`/expenses/exchange-rate?currency=${encodeURIComponent(currency)}&date=${encodeURIComponent(date)}`),
  scanQuota: () => request<{ used: number; limit: number | null }>('/expenses/scan-quota'),
  bankOverview: () => request<BankOverviewDto>('/bank'),
  bankInstitutions: () => request<BankInstitutionDto[]>('/bank/institutions'),
  bankConnect: (institutionId: string) =>
    request<{ authorisationUrl: string }>('/bank/connections', { method: 'POST', ...body({ institutionId }) }),
  bankSync: (id: string) => request<{ accounts: number; added: number }>(`/bank/connections/${id}/sync`, { method: 'POST' }),
  bankDisconnect: (id: string) => request<void>(`/bank/connections/${id}`, { method: 'DELETE' }),
  bankTransactions: (status: BankTransactionFilter) => request<BankTransactionDto[]>(`/bank/transactions?status=${status}`),
  bankAcceptSafe: () => request<{ matched: number }>('/bank/transactions/accept-safe', { method: 'POST' }),
  bankCreateAccount: (data: { name: string; bankName?: string | null; identification?: string | null }) =>
    request<{ id: string }>('/bank/accounts', { method: 'POST', ...body(data) }),
  bankDeleteAccount: (id: string) => request<void>(`/bank/accounts/${id}`, { method: 'DELETE' }),
  bankImport: (accountId: string, file: File) =>
    request<BankImportResultDto>(`/bank/accounts/${accountId}/import`, { method: 'POST', body: formData(file) }),
  bankDeleteImport: (id: string) => request<void>(`/bank/imports/${id}`, { method: 'DELETE' }),
  bankMatch: (id: string, type: 'invoice' | 'expense', targetId: string) =>
    request<unknown>(`/bank/transactions/${id}/match`, { method: 'POST', ...body({ type, targetId }) }),
  bankUnmatch: (id: string) => request<unknown>(`/bank/transactions/${id}/unmatch`, { method: 'POST' }),
  bankIgnore: (id: string) => request<unknown>(`/bank/transactions/${id}/ignore`, { method: 'POST' }),
  signatures: () => request<SignatureOverviewDto>('/signatures'),
  sendForSignature: (data: { title: string; message?: string | null; quoteId?: string | null; signers: { name: string; email: string }[] }, file: File | null) => {
    const form = file ? formData(file) : new FormData();
    form.append('data', JSON.stringify(data));
    return request<SignatureRequestDto>('/signatures', { method: 'POST', body: form });
  },
  refreshSignature: (id: string) => request<SignatureRequestDto>(`/signatures/${id}/refresh`, { method: 'POST' }),
  remindSignature: (id: string) => request<{ sent: number }>(`/signatures/${id}/remind`, { method: 'POST' }),
  cancelSignature: (id: string) => request<SignatureRequestDto>(`/signatures/${id}/cancel`, { method: 'POST' }),
  phoneLine: () => request<PhoneLineDto>('/leads/line'),
  updatePhoneLine: (data: { greeting?: string | null; whatsappEnabled?: boolean }) => request<{ ok: true }>('/leads/line', { method: 'PATCH', ...body(data) }),
  leads: (status?: LeadStatus) => request<LeadDto[]>(`/leads${status ? `?status=${status}` : ''}`),
  lead: (id: string) => request<LeadDto>(`/leads/${id}`),
  updateLead: (id: string, data: { status?: LeadStatus; quoteId?: string | null }) => request<{ ok: true }>(`/leads/${id}`, { method: 'PATCH', ...body(data) }),
  deleteLead: (id: string) => request<void>(`/leads/${id}`, { method: 'DELETE' }),
  analyzeLead: (id: string) => request<LeadDto>(`/leads/${id}/analyze`, { method: 'POST' }),
  adminPhoneLines: () => request<{ id: string; e164: string; display: string; whatsappEnabled: boolean; email: string; userId: string }[]>('/admin/phone-lines'),
  adminAssignPhoneLine: (data: { email: string; e164: string; whatsappEnabled: boolean }) =>
    request<{ ok: true }>('/admin/phone-lines', { method: 'POST', ...body(data) }),
  adminDeletePhoneLine: (id: string) => request<void>(`/admin/phone-lines/${id}`, { method: 'DELETE' }),
  accountantAccess: () => request<AccountantAccessDto>('/accountant/access'),
  accountantInvite: (email: string) => request<{ ok: true }>('/accountant/access', { method: 'POST', ...body({ email }) }),
  accountantRevoke: () => request<void>('/accountant/access', { method: 'DELETE' }),
  accountantInvitation: (token: string) => request<AccountantInvitationDto>(`/accountant/invitations/${encodeURIComponent(token)}`),
  accountantAccept: (token: string, data: { password: string; firstName?: string | null; lastName?: string | null; firmName?: string | null }) =>
    request<UserDto>(`/accountant/invitations/${encodeURIComponent(token)}/accept`, { method: 'POST', ...body(data) }),
  accountantDossiers: () => request<AccountantDossierDto[]>('/accountant/dossiers'),
  accountantSummary: (ownerId: string, from: string, to: string) =>
    request<AccountingSummaryDto>(`/accountant/dossiers/${ownerId}/summary?${new URLSearchParams({ from, to })}`),
  accountantEntries: (ownerId: string, from: string, to: string) =>
    request<{ entries: LedgerEntryDto[]; overrides: LedgerOverrideDto[] }>(`/accountant/dossiers/${ownerId}/entries?${new URLSearchParams({ from, to })}`),
  accountantInvoices: (ownerId: string, from: string, to: string) =>
    request<{ id: string; number: string; documentType: string; issueDate: string; status: string; client: string; subtotal: number; tax: number; total: number }[]>(
      `/accountant/dossiers/${ownerId}/invoices?${new URLSearchParams({ from, to })}`,
    ),
  accountantExpenses: (ownerId: string, from: string, to: string) =>
    request<
      {
        id: string;
        purchasedOn: string;
        paidOn: string | null;
        supplier: string;
        description: string;
        category: string;
        amountExclTax: number | null;
        taxAmount: number | null;
        amountInclTax: number;
        attachmentId: string | null;
        paymentMethod: string | null;
      }[]
    >(`/accountant/dossiers/${ownerId}/expenses?${new URLSearchParams({ from, to })}`),
  accountantReclassify: (ownerId: string, data: { entryKey: string; accountFrom: string; accountTo: string; accountLabel?: string | null }) =>
    request<{ ok: true }>(`/accountant/dossiers/${ownerId}/overrides`, { method: 'POST', ...body(data) }),
  accountantRevert: (ownerId: string, id: string) => request<void>(`/accountant/dossiers/${ownerId}/overrides/${id}`, { method: 'DELETE' }),
  accountingSummary: (from: string, to: string) =>
    request<AccountingSummaryDto>(`/accounting/summary?${new URLSearchParams({ from, to })}`),
  expenseSummary: (year: number | 'all') => request<ExpenseSummaryDto>(`/expenses/summary?year=${year}`),
  einvoiceReception: () => request<EinvoiceReceptionDto>('/expenses/einvoice-reception'),
  activateEinvoiceReception: () => request<EinvoiceReceptionDto>('/expenses/einvoice-reception', { method: 'POST' }),
  syncEinvoices: () => request<{ imported: number; skipped: number }>('/expenses/einvoice-sync', { method: 'POST' }),
  features: () => request<FeaturesDto>('/me/features'),
  subscription: () => request<SubscriptionDto>('/me/subscription'),
  getPreferences: () => request<UserPreferencesDto>('/me/preferences'),
  updatePreferences: (data: Partial<UserPreferencesDto>) =>
    request<UserPreferencesDto>('/me/preferences', { method: 'PATCH', ...body(data) }),

  listCompanies: (type?: 'provider' | 'client') =>
    request<CompanyDto[]>(`/companies${type ? `?type=${type}` : ''}`),
  lookupSiret: (siret: string) => request<CompanyDraft>(`/companies/lookup-siret/${siret}`),
  createCompany: (data: unknown) => request<CompanyDto>('/companies', { method: 'POST', ...body(data) }),
  billing: () => request<BillingDto>('/billing'),
  billingCheckout: (plan: PlanId, interval: BillingInterval = 'month') => request<{ url: string }>('/billing/checkout', { method: 'POST', ...body({ plan, interval }) }),
  billingPortal: () => request<{ url: string }>('/billing/portal', { method: 'POST' }),
  billingSync: () => request<{ ok: boolean }>('/billing/sync', { method: 'POST' }),
  updateCompany: (id: string, data: unknown) =>
    request<CompanyDto>(`/companies/${id}`, { method: 'PATCH', ...body(data) }),
  deleteCompany: (id: string) => request<void>(`/companies/${id}`, { method: 'DELETE' }),

  listInvoices: () => request<InvoiceDto[]>('/invoices'),
  getInvoice: (id: string) => request<InvoiceDto>(`/invoices/${id}`),
  aiDraftStatus: () => request<{ enabled: boolean; quota: AiQuotaDto }>('/invoices/ai-draft'),

  getCatalog: () => request<CatalogItemDto[]>('/catalog'),
  saveCatalog: (items: Omit<CatalogItemDto, 'id'>[]) => request<CatalogItemDto[]>('/catalog', { method: 'PUT', ...body({ items }) }),

  listQuotes: () => request<QuoteDto[]>('/quotes'),
  getQuote: (id: string) => request<QuoteDto>(`/quotes/${id}`),
  createQuote: (data: unknown) => request<QuoteDto>('/quotes', { method: 'POST', ...body(data) }),
  updateQuote: (id: string, data: unknown) => request<QuoteDto>(`/quotes/${id}`, { method: 'PATCH', ...body(data) }),
  deleteQuote: (id: string) => request<void>(`/quotes/${id}`, { method: 'DELETE' }),
  sendQuote: (id: string, email: boolean) =>
    request<{ quote: QuoteDto; link: string; emailed: boolean }>(`/quotes/${id}/send`, { method: 'POST', ...body({ email }) }),
  decideQuote: (id: string, decision: 'accepted' | 'declined', reason?: string) =>
    request<QuoteDto>(`/quotes/${id}/decision`, { method: 'POST', ...body({ decision, reason: reason || null }) }),
  acceptQuoteOnSite: (id: string, data: QuoteOnSiteAcceptInput) =>
    request<{ quote: QuoteDto; emailed: boolean }>(`/quotes/${id}/accept-on-site`, { method: 'POST', ...body(data) }),
  reviseQuote: (id: string) => request<QuoteDto>(`/quotes/${id}/revise`, { method: 'POST' }),
  invoiceQuote: (id: string) => request<{ invoiceId: string }>(`/quotes/${id}/invoice`, { method: 'POST' }),
  quoteAiStatus: () => request<{ enabled: boolean; voice: boolean; quota: AiQuotaDto }>('/quotes/ai-draft'),
  /** Le champ `clientId` précède le fichier : le multipart les lit dans l'ordre. */
  transcribeBrief: (file: File, clientId: string) => {
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new ApiRequestError(413, 'Enregistrement trop volumineux (10 Mo au maximum, soit une dizaine de minutes).', null);
    }
    const data = new FormData();
    if (clientId) data.append('clientId', clientId);
    data.append('file', file, file.name);
    return request<{ text: string; seconds: number }>('/quotes/transcribe', { method: 'POST', body: data });
  },
  generateQuoteDraft: (data: { clientId: string; instructions: string }) =>
    request<QuoteAiProposal>('/quotes/ai-draft', { method: 'POST', ...body(data) }),

  publicQuote: (token: string) => request<PublicQuoteDto>(`/public/quotes/${encodeURIComponent(token)}`),
  requestQuoteCode: (token: string) =>
    request<{ sentTo: string }>(`/public/quotes/${encodeURIComponent(token)}/code`, { method: 'POST' }),
  acceptQuote: (token: string, name: string, code: string) =>
    request<{ accepted: true }>(`/public/quotes/${encodeURIComponent(token)}/accept`, { method: 'POST', ...body({ name, code }) }),
  payPublicQuoteDeposit: (token: string) =>
    request<{ url: string }>(`/public/quotes/${encodeURIComponent(token)}/deposit`, { method: 'POST' }),
  reminders: (targetType: 'quote' | 'invoice', targetId: string) =>
    request<{ enabled: boolean; reminders: ReminderDto[] }>(`/reminders?targetType=${targetType}&targetId=${targetId}`),
  sendReminder: (targetType: 'quote' | 'invoice', targetId: string) =>
    request<{ sentTo: string }>('/reminders', { method: 'POST', ...body({ targetType, targetId }) }),
  setReminderEnabled: (targetType: 'quote' | 'invoice', targetId: string, enabled: boolean) =>
    request<{ enabled: boolean }>('/reminders/settings', { method: 'PATCH', ...body({ targetType, targetId, enabled }) }),
  bookingSettings: () => request<BookingSettingsDto>('/booking/settings'),
  saveBookingSettings: (data: BookingSettingsInput) => request<BookingSettingsDto>('/booking/settings', { method: 'PUT', ...body(data) }),
  appointments: (from: string, to: string) => request<AppointmentDto[]>(`/booking/appointments?from=${from}&to=${to}`),
  dashboard: () => request<DashboardDto>('/dashboard'),
  calendarFeed: () => request<{ url: string }>('/booking/calendar'),
  resetCalendarFeed: () => request<{ url: string }>('/booking/calendar/reset', { method: 'POST' }),
  createAppointment: (data: ManualAppointmentInput) =>
    request<{ appointment: AppointmentDto; warning: string | null }>('/booking/appointments', { method: 'POST', ...body(data) }),
  setAppointmentStatus: (id: string, status: AppointmentDto['status']) =>
    request<AppointmentDto>(`/booking/appointments/${id}`, { method: 'PATCH', ...body({ status }) }),
  publicBooking: (slug: string) => request<PublicBookingDto>(`/public/booking/${encodeURIComponent(slug)}`),
  publicBookingSlots: (slug: string, serviceId: string, address: string) =>
    request<{ address: string; days: { date: string; times: string[] }[] }>(`/public/booking/${encodeURIComponent(slug)}/slots`, { method: 'POST', ...body({ serviceId, address }) }),
  publicBook: (slug: string, data: BookingRequestInput) =>
    request<{ when: string }>(`/public/booking/${encodeURIComponent(slug)}/book`, { method: 'POST', ...body(data) }),
  publicBookingCancelInfo: (token: string) =>
    request<{ when: string; serviceLabel: string; status: string }>(`/public/booking/cancel/${encodeURIComponent(token)}`),
  publicBookingCancel: (token: string) => request<{ cancelled: boolean }>(`/public/booking/cancel/${encodeURIComponent(token)}`, { method: 'POST' }),
  connectStatus: () => request<ConnectStatusDto>('/payments/connect'),
  connectOnboarding: () => request<{ url: string }>('/payments/connect/onboarding', { method: 'POST' }),
  connectRefresh: () => request<{ chargesEnabled: boolean }>('/payments/connect/refresh', { method: 'POST' }),
  connectDashboard: () => request<{ url: string }>('/payments/connect/dashboard', { method: 'POST' }),
  quoteDepositCheckout: (id: string) => request<{ url: string }>(`/payments/quotes/${id}/deposit`, { method: 'POST' }),
  declineQuote: (token: string, reason: string) =>
    request<{ declined: true }>(`/public/quotes/${encodeURIComponent(token)}/decline`, { method: 'POST', ...body({ reason: reason || null }) }),
  generateAiDraft: (data: { clientId: string; instructions: string }) =>
    request<AiDraftProposal>('/invoices/ai-draft', { method: 'POST', ...body(data) }),
  createInvoice: (data: unknown) => request<InvoiceDto>('/invoices', { method: 'POST', ...body(data) }),
  updateInvoice: (id: string, data: unknown) =>
    request<InvoiceDto>(`/invoices/${id}`, { method: 'PATCH', ...body(data) }),
  deleteInvoice: (id: string) => request<void>(`/invoices/${id}`, { method: 'DELETE' }),
  sendInvoice: (id: string) => request<InvoiceDto>(`/invoices/${id}/send`, { method: 'POST' }),
  createCreditNote: (id: string, data: unknown) => request<InvoiceDto>(`/invoices/${id}/credit-note`, { method: 'POST', ...body(data) }),
  notifyPayment: (id: string) =>
    request<InvoiceDto>(`/invoices/${id}/payment-notification`, { method: 'POST' }),
  refreshInvoiceStatus: (id: string) =>
    request<InvoiceDto>(`/invoices/${id}/refresh-status`, { method: 'POST' }),

  adminSettings: () => request<AppSettingsDto>('/admin/settings'),
  adminUpdateSettings: (data: Partial<AppSettingsDto>) =>
    request<AppSettingsDto>('/admin/settings', { method: 'PATCH', ...body(data) }),
  adminUsers: () => request<AdminUserDto[]>('/admin/users'),
  adminCreateUser: (data: unknown) =>
    request<AdminUserDto>('/admin/users', { method: 'POST', ...body(data) }),
  adminSetRole: (id: string, role: 'admin' | 'user') =>
    request<{ id: string; role: string }>(`/admin/users/${id}`, { method: 'PATCH', ...body({ role }) }),
  adminSetEnvironment: (id: string, environment: 'production' | 'sandbox') =>
    request<{ id: string; environment: string }>(`/admin/users/${id}/environment`, { method: 'PATCH', ...body({ environment }) }),
  adminSetSubscription: (id: string, status: SubscriptionStatus, plan: PlanId | null = null) =>
    request<{ id: string; subscriptionStatus: SubscriptionStatus; plan: PlanId | null }>(`/admin/users/${id}/subscription`, {
      method: 'PATCH',
      ...body({ status, plan }),
    }),
  adminApprove: (id: string) =>
    request<{ id: string; approved: true }>(`/admin/users/${id}/approve`, { method: 'POST' }),
  adminAudit: (userId?: string) =>
    request<AuditEventDto[]>(`/admin/audit?limit=100${userId ? `&userId=${encodeURIComponent(userId)}` : ''}`),
  adminOpenapiUsage: (period: UsagePeriod, date: string) =>
    request<OpenapiUsageDto>(`/admin/openapi-usage?period=${period}&date=${encodeURIComponent(date)}`),
  adminDeleteUser: (id: string) => request<void>(`/admin/users/${id}`, { method: 'DELETE' }),
};
